/**
 * MODULE: _shared/frontend-shell/src/layout/mobileShell/MobileShellProvider.tsx
 * PURPOSE: Mobile-shell React context inside AppLayout; syncs html flag (also set by MobileShellDocumentSync at app root).
 * PUBLIC_API: MobileShellProvider, useMobileShell, MobileShellContextValue
 * SIDE_EFFECTS: document.documentElement data attribute; matchMedia listeners via useMobileShellState
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 55 / 150
 * RECENT: 2026-06-09 [2.2.1361] — removed unmount cleanup that cleared html flag; DocumentSync handles public routes.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';

import { useMobileShellState } from './useMobileShellState';

export type MobileShellContextValue = {
  /** Narrow viewport (≤767px) — KPI strips, card layouts, filter sheets. */
  isNarrowViewport: boolean;
  /** Mobile shell chrome: bottom nav, hidden sidebar rail, compact main padding. */
  isMobileShell: boolean;
};

const MobileShellContext = createContext<MobileShellContextValue>({
  isNarrowViewport: false,
  isMobileShell: false,
});

function syncMobileShellDocumentFlag(active: boolean): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.mobileShell = active ? 'true' : 'false';
}

export function MobileShellProvider({ children }: { children: ReactNode }) {
  const state = useMobileShellState();

  useEffect(() => {
    syncMobileShellDocumentFlag(state.isMobileShell);
  }, [state.isMobileShell]);

  const value = useMemo(
    () => state,
    [state.isMobileShell, state.isNarrowViewport]
  );

  return (
    <MobileShellContext.Provider value={value}>{children}</MobileShellContext.Provider>
  );
}

export function useMobileShell(): MobileShellContextValue {
  return useContext(MobileShellContext);
}
